/* eslint-disable no-unused-vars, jsx-a11y/alt-text, react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiBell,
    FiCheck,
    FiCheckCircle,
    FiX,
    FiClock,
    FiPackage,
    FiAlertTriangle,
    FiDollarSign,
    FiInfo,
    FiSettings,
    FiVolume2,
    FiVolumeX
} from 'react-icons/fi';
import { useNotifications } from '../context/NotificationContext';

const NotificationCenter = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const {
        notifications,
        unreadCount,
        isConnected,
        settings,
        markAsRead,
        markAllAsRead,
        updateSettings
    } = useNotifications();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'new_order':
                return { icon: FiPackage, color: 'text-emerald-500', bg: 'bg-emerald-50' };
            case 'order_status':
                return { icon: FiCheckCircle, color: 'text-blue-500', bg: 'bg-blue-50' };
            case 'low_stock':
                return { icon: FiAlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' };
            case 'expiry_warning':
                return { icon: FiClock, color: 'text-red-500', bg: 'bg-red-50' };
            case 'payment_received':
                return { icon: FiDollarSign, color: 'text-emerald-500', bg: 'bg-emerald-50' };
            default:
                return { icon: FiInfo, color: 'text-slate-500', bg: 'bg-slate-50' };
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'الآن';
        if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
        if (diffHours < 24) return `منذ ${diffHours} ساعة`;
        if (diffDays < 7) return `منذ ${diffDays} يوم`;
        return date.toLocaleDateString('ar-SA-u-nu-latn');
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-xl hover:bg-slate-100 transition-all duration-200"
            >
                <FiBell size={22} className="text-slate-600" />

                {/* Unread Badge */}
                {unreadCount > 0 && (
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold"
                    >
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>
                )}

                {/* Connection Status Dot */}
                <span
                    className={`absolute bottom-0 right-0 w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-amber-500'
                        }`}
                />
            </button>

            {/* Dropdown */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute left-0 top-full mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50"
                        style={{ direction: 'rtl' }}
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FiBell className="text-slate-600" />
                                <h3 className="font-bold text-slate-800">الإشعارات</h3>
                                {unreadCount > 0 && (
                                    <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-bold">
                                        {unreadCount} جديد
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                {/* Sound Toggle */}
                                <button
                                    onClick={() =>
                                        updateSettings({ soundEnabled: !settings.soundEnabled })
                                    }
                                    className={`p-2 rounded-lg transition ${settings.soundEnabled
                                            ? 'bg-emerald-50 text-emerald-600'
                                            : 'bg-slate-50 text-slate-400'
                                        }`}
                                    title={settings.soundEnabled ? 'كتم الصوت' : 'تفعيل الصوت'}
                                >
                                    {settings.soundEnabled ? (
                                        <FiVolume2 size={16} />
                                    ) : (
                                        <FiVolumeX size={16} />
                                    )}
                                </button>

                                {/* Mark All Read */}
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllAsRead}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-bold"
                                    >
                                        تحديد الكل كمقروء
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Notifications List */}
                        <div className="max-h-[400px] overflow-y-auto">
                            {notifications.length === 0 ? (
                                <div className="p-8 text-center">
                                    <FiBell className="text-slate-200 mx-auto mb-3" size={40} />
                                    <p className="text-slate-500">لا توجد إشعارات</p>
                                </div>
                            ) : (
                                notifications.slice(0, 20).map((notification) => {
                                    const {
                                        icon: Icon,
                                        color,
                                        bg
                                    } = getNotificationIcon(notification.type);

                                    return (
                                        <motion.div
                                            key={notification.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className={`p-4 border-b border-slate-50 hover:bg-slate-50 transition cursor-pointer ${!notification.is_read ? 'bg-blue-50/30' : ''
                                                }`}
                                            onClick={() =>
                                                !notification.is_read && markAsRead(notification.id)
                                            }
                                        >
                                            <div className="flex gap-3">
                                                <div className={`p-2 ${bg} rounded-xl ${color}`}>
                                                    <Icon size={20} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <h4
                                                            className={`font-bold text-sm ${!notification.is_read
                                                                    ? 'text-slate-800'
                                                                    : 'text-slate-600'
                                                                }`}
                                                        >
                                                            {notification.title}
                                                        </h4>
                                                        {!notification.is_read && (
                                                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                                        {notification.message}
                                                    </p>
                                                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                                                        <FiClock size={12} />
                                                        {formatTime(notification.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })
                            )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                            <div className="p-3 border-t border-slate-100 bg-slate-50">
                                <button className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-bold py-2 hover:bg-blue-50 rounded-xl transition">
                                    عرض جميع الإشعارات
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationCenter;
